/// <amd-module name="Wit/WorkItemModel/Services/WorkItemFormUnsavedChangesService" />
import * as React from "react";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { FastPageSwitchEventDetail, IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { Observer } from "VSSUI/Observer";
import { IWorkItem, IWorkItemSavingFailedError, IWorkItemsBulkSaveSuccessResult } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as Resources from "Wit/WorkItemModel/Resources";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";

/**
 * Service for protecting a user against accidentally losing unsaved changes.
 */
export interface IWorkItemFormUnsavedChangesService extends IVssService {
    /**
     * Shows the unsaved changes dialog and returns a promise will resolve to true if navigation should happen
     */
    showMessage(workItem?: IWorkItem, saveCallback?: () => Promise<void>): Promise<boolean>;

    /**
     * Register a work item that may have unsaved changes. Prompt the user
     * to confirm losing changes before navigating away
     *
     * @param workItem The work item to register
     */
    register(workItem: IWorkItem): void;

    /**
     * Register a filter that will be run on FPS to determine if the FPS
     * Starting handler should run
     *
     * @param filter function that inspects event and returns true if the handler should run
     */
    registerFPSFilter(filter: (e: CustomEvent<FastPageSwitchEventDetail>) => boolean): void;

    /**
     * Unregister a work item
     *
     * @param workItem The work item to unregister
     */
    unregister(workItem: IWorkItem): void;

    /**
     * Checks if unsaved changes dialog is active
     */
    isUnsavedChangesDialogActive(): boolean;
}

class WorkItemFormUnsavedChangesService extends VssService implements IWorkItemFormUnsavedChangesService {
    private workItems: IWorkItem[] = [];
    private saving = new ObservableValue<boolean>(false);
    private unsavedChangesDialogShown = false;
    private errorUnsavedWorkItems = new ObservableArray<IWorkItem>([]);
    private filters = new Set<(e: CustomEvent<FastPageSwitchEventDetail>) => boolean>();

    public isUnsavedChangesDialogActive(): boolean {
        return this.unsavedChangesDialogShown;
    }
    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        document.body.addEventListener("fpsStarting", this.onFpsStarting);
        window.addEventListener("beforeunload", this.onBeforeUnload);
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        document.body.removeEventListener("fpsStarting", this.onFpsStarting);
        window.removeEventListener("beforeunload", this.onBeforeUnload);
        this.filters.clear();
    }

    public register(workItem: IWorkItem): void {
        if (!this.workItems.includes(workItem)) {
            this.workItems.push(workItem);
        }
    }

    public unregister(workItem: IWorkItem): void {
        this.workItems = this.workItems.filter(wi => wi !== workItem);
    }

    public registerFPSFilter(filter: (e: CustomEvent<FastPageSwitchEventDetail>) => boolean): void {
        this.filters.add(filter);
    }

    public async showMessage(workItem?: IWorkItem, saveCallback?: () => Promise<void>): Promise<boolean> {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        if (this.unsavedChangesDialogShown) {
            return Promise.resolve(false);
        }

        this.unsavedChangesDialogShown = true;
        return new Promise(resolve =>
            layoutManager.renderCallout(onDismiss => (
                <Observer errorUnsavedWorkItems={this.errorUnsavedWorkItems} saving={this.saving}>
                    {(props: { errorUnsavedWorkItems: IWorkItem[]; saving: boolean }) => {
                        return (
                            <Dialog
                                footerButtonProps={[
                                    {
                                        text: Resources.Cancel,
                                        onClick: () => {
                                            this.resolveDialog(false, onDismiss, resolve);
                                        }
                                    },
                                    {
                                        text: Resources.DiscardChanges,
                                        onClick: () => {
                                            workItem ? this.resetWorkItem(workItem) : this.resetWorkItems();
                                            this.resolveDialog(true, onDismiss, resolve);
                                        }
                                    },
                                    {
                                        onClick: () => {
                                            const onSave = () => {
                                                this.saving.value = false;
                                                this.resolveDialog(true, onDismiss, resolve);
                                            };

                                            const onError = (error: IWorkItemSavingFailedError) => {
                                                this.saving.value = false;
                                                this.errorUnsavedWorkItems.value = error.results
                                                    ? error.results.filter(result => !!result.error).map(result => result.workItem)
                                                    : [];
                                            };

                                            if (workItem && saveCallback) {
                                                this.saveWorkItem(onSave, onError, saveCallback);
                                                return;
                                            }

                                            this.saveWorkItems(onSave, onError);
                                        },
                                        primary: true,
                                        text: Resources.SaveChanges
                                    }
                                ]}
                                modal={true}
                                onDismiss={() => {
                                    this.resolveDialog(false, onDismiss, resolve);
                                }}
                                overlay={props.saving ? { spinnerLabel: Resources.SavingWorkItems } : undefined}
                                titleProps={{ text: Resources.DirtyNavDialogTitle }}
                            >
                                <div className="padding-vertical-4">
                                    <div>{Resources.DirtyNavDialogUnsavedChanges}</div>
                                    <div>
                                        {workItem
                                            ? this.getWorkItemTitle(workItem)
                                            : this.getWorkItemTitles(this.workItems.filter(workitem => workitem.dirty.value))}
                                    </div>
                                </div>
                                <div className="padding-vertical-4">{Resources.DirtyNavDialogOptions}</div>
                                {this.errorUnsavedWorkItems.length !== 0 && (
                                    <div className="error-text padding-vertical-4">
                                        <div>{Resources.DirtyNavDialogSavingFailure} </div>
                                        <div>{this.getWorkItemTitles(props.errorUnsavedWorkItems)}</div>
                                    </div>
                                )}
                            </Dialog>
                        );
                    }}
                </Observer>
            ))
        );
    }

    private resolveDialog = (shouldContinue: boolean, onDismiss: () => void, resolve: (value: boolean) => void) => {
        this.unsavedChangesDialogShown = false;
        onDismiss();
        resolve(shouldContinue);
    };

    private onBeforeUnload = (e: BeforeUnloadEvent): void => {
        if (this.isDirty()) {
            e.returnValue = "dirtyState";
            e.preventDefault();
        }
    };

    private onFpsStarting = (e: CustomEvent<FastPageSwitchEventDetail>): void => {
        const skipEvent = [...this.filters.values()].some(filter => !filter(e));
        if (!skipEvent && this.isDirty() && !(e.detail.data && e.detail.data.userConfirmed)) {
            e.preventDefault();
            e.detail.cancellationReason = "dirtyState";
            this.showMessage().then(shouldContinue => {
                shouldContinue &&
                    FastPageSwitch(e.detail.pageContext, e.detail.href, e.detail.recordHistoryEntry, { ...e.detail.data, userConfirmed: true });
            });
        }
    };

    private isDirty = (): boolean => {
        for (const workItem of this.workItems) {
            if (workItem.dirty.value) {
                return true;
            }
        }
        return false;
    };

    private saveWorkItems = (
        onSave: (result: IWorkItemsBulkSaveSuccessResult) => void,
        onError: (error: IWorkItemSavingFailedError) => void
    ): void => {
        const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");

        this.saving.value = true;
        savingService.saveWorkItemsBatchAsync(this.workItems.filter(workitem => workitem.dirty.value)).then(onSave, onError);
    };

    private saveWorkItem = (onSave: () => void, onError: (error: IWorkItemSavingFailedError) => void, saveCallback: () => Promise<void>): void => {
        this.saving.value = true;
        saveCallback().then(onSave).catch(onError);
    };

    private resetWorkItems = (): void => {
        for (const workItem of this.workItems) {
            workItem.resetChanges();
        }
    };

    private resetWorkItem = (workItem: IWorkItem): void => {
        workItem.resetChanges();
    };

    private getWorkItemTitles = (workItems: IWorkItem[]): string => {
        return workItems.map(wi => wi.getCaption({ excludeTempId: true })).join(", ");
    };

    private getWorkItemTitle = (workItem: IWorkItem): string => {
        return workItem.getCaption({ excludeTempId: true });
    };
}

Services.add("IWorkItemFormUnsavedChangesService", { serviceFactory: WorkItemFormUnsavedChangesService });
